/**
 * TAKING THE OFFICER TO A PLACE IN THE ORDER.
 *
 * A next step is about a passage; the order beside it is twenty pages. Both
 * flows answer "where?" the same way: scroll the order pane to the passage and
 * mark it for a moment. How the passage is FOUND differs (the appellate flow
 * looks for the question a point asks — `features/oia/lib/locate-in-order.ts`;
 * the adjudication flow knows the section number), so only the showing is
 * shared.
 */
const LOCATED_CLASS = "doc-located"

/** Scrolls to the element and marks it briefly (the mark restarts on a second click). */
export function revealInOrder(target: HTMLElement): void {
  target.scrollIntoView?.({ behavior: "smooth", block: "center" })
  target.classList.remove(LOCATED_CLASS)
  void target.offsetWidth
  target.classList.add(LOCATED_CLASS)
  window.setTimeout(() => target.classList.remove(LOCATED_CLASS), 2400)
}

const squash = (text: string | null | undefined) => (text ?? "").replace(/\s+/g, " ").trim()

/** "5.2" opens a heading or a bold sub-heading: "5.2 Allegation…", "5.2. …", "**5.2** …". */
function opensWith(el: Element, section: string): boolean {
  const text = squash(el.textContent)
  if (!text.startsWith(section)) return false
  const next = text.charAt(section.length)
  // "5.2" must not match "5.21" or "5.2.1".
  return next === "" || !/[\d]/.test(next) && !(next === "." && /\d/.test(text.charAt(section.length + 1)))
}

/**
 * The element where section `5.2` begins, else where its parent `5` begins,
 * else `null`. Headings first; then a paragraph that opens with the number (the
 * numbered-paragraph style prints "5.2" as the first words of a paragraph).
 */
export function findSection(root: ParentNode, section: string): HTMLElement | null {
  const wanted = section.trim().replace(/\.$/, "")
  if (!wanted) return null
  const headings = Array.from(root.querySelectorAll<HTMLElement>("h1, h2, h3, h4, h5"))
  const paragraphs = Array.from(root.querySelectorAll<HTMLElement>("p"))
  for (let s = wanted; s; s = s.includes(".") ? s.slice(0, s.lastIndexOf(".")) : "") {
    const hit = headings.find((h) => opensWith(h, s)) ?? paragraphs.find((p) => opensWith(p, s))
    if (hit) return hit
  }
  return null
}

/** False = the order does not print that section. */
export function showSection(section: string, root: ParentNode | null): boolean {
  const target = root ? findSection(root, section) : null
  if (!target) return false
  revealInOrder(target)
  return true
}
